import request from '@/router/axios'

/** 与后端 InventoryVisualizationController 一致：/api 由网关前缀 */
const base = '/api/produce/inventory-visualization'

/**
 * 仅当需要离线演示时设置：VUE_APP_INVENTORY_VIS_USE_MOCK=true
 */
const USE_MOCK = process.env.VUE_APP_INVENTORY_VIS_USE_MOCK === 'true'

const MOCK_STOCK_CODES = ['WH01', 'WH02', 'WH03', 'WH04']
const MOCK_CARGO_NOS = ['C-DL', 'C-JM', 'C-WY', 'C-HM']
const AGE_BUCKETS = ['0-7天', '8-15天', '16-30天', '30天以上']

function bucketForDays(d) {
  const n = Number(d) || 0
  if (n <= 7) return '0-7天'
  if (n <= 15) return '8-15天'
  if (n <= 30) return '16-30天'
  return '30天以上'
}

/** 与「智能库存准确性分析」等页一致：仓库拼音码 */
export function searchStorePycode(storeName) {
  return request({
    url: '/api/produce/store/select/distinct/pycode',
    method: 'get',
    params: { storeName: storeName || '' }
  })
}

/** 与项目物资拼音码一致：vardict */
export function searchVarPycode(varName) {
  return request({
    url: '/api/produce/vardict/select/distinct/pycode',
    method: 'get',
    params: { varName: varName || '' }
  })
}

function normalize(params = {}) {
  return {
    stockCode: params.stockCode || '',
    cargoNo: params.cargoNo || '',
    stockDate: params.stockDate || ''
  }
}

function buildMockRows(params = {}) {
  const p = normalize(params)
  const rows = []
  let seq = 1
  MOCK_STOCK_CODES.forEach((stockCode, shedIdx) => {
    MOCK_CARGO_NOS.forEach((cargoNo, coalIdx) => {
      const baseVal = 5200 + shedIdx * 1400 + coalIdx * 900
      const fluctuation = ((shedIdx + 1) * (coalIdx + 2) * 7) % 850
      const tonnage = baseVal + fluctuation
      const storageDays = 4 + ((shedIdx * 5 + coalIdx * 11) % 37)
      const riskLevel = storageDays > 30 ? '高' : storageDays > 15 ? '中' : '低'
      rows.push({
        id: seq++,
        shed: `${stockCode}棚`,
        coalType: `${cargoNo}煤`,
        stockCode,
        cargoNo,
        tonnage,
        storageDays,
        riskLevel,
        stockRate: Number((tonnage / 100000).toFixed(3))
      })
    })
  })
  return rows.filter((r) => {
    if (p.stockCode && r.stockCode !== p.stockCode) return false
    if (p.cargoNo && r.cargoNo !== p.cargoNo) return false
    return true
  })
}

function mockResolve(data) {
  return Promise.resolve({ data: { code: 200, msg: 'success', data } })
}

function mockDashboardData(params) {
  const rows = buildMockRows(params)
  const totalTonnage = rows.reduce((sum, row) => sum + row.tonnage, 0)
  const avgStockDays = rows.length
    ? Number((rows.reduce((sum, row) => sum + row.storageDays, 0) / rows.length).toFixed(1))
    : 0
  const highRiskCount = rows.filter((row) => row.riskLevel === '高').length
  const shedCount = new Set(rows.map((r) => r.stockCode)).size
  const summary = { totalTonnage, avgStockDays, highRiskCount, shedCount }

  const coalMap = {}
  rows.forEach((row) => {
    coalMap[row.cargoNo] = (coalMap[row.cargoNo] || 0) + row.tonnage
  })
  const coalTotal = Object.values(coalMap).reduce((sum, n) => sum + n, 0)
  const coalDistribution = {
    items: Object.keys(coalMap).map((k) => ({
      code: k,
      name: `${k}（物资名）`,
      value: coalMap[k],
      percent: coalTotal ? Number(((coalMap[k] / coalTotal) * 100).toFixed(2)) : 0
    }))
  }

  const ageMap = {}
  AGE_BUCKETS.forEach((a) => {
    ageMap[a] = 0
  })
  rows.forEach((row) => {
    const b = bucketForDays(row.storageDays)
    ageMap[b] += row.tonnage
  })
  const storageAging = {
    buckets: AGE_BUCKETS.map((name) => ({ name, value: ageMap[name] }))
  }

  const shedMap = {}
  rows.forEach((row) => {
    shedMap[row.stockCode] = (shedMap[row.stockCode] || 0) + row.tonnage
  })
  const shedStock = {
    items: MOCK_STOCK_CODES.map((stockCode) => ({
      code: stockCode,
      name: `${stockCode}煤棚`,
      value: shedMap[stockCode] || 0
    }))
  }

  return { summary, coalDistribution, storageAging, shedStock }
}

/**
 * 看板聚合：KPI + 煤种分布 + 堆存分布 + 煤棚对比（对应后端 POST /dashboard）。
 */
export function fetchDashboard(params) {
  if (USE_MOCK) {
    return mockResolve(mockDashboardData(params))
  }
  return request({ url: `${base}/dashboard`, method: 'post', data: params })
}

/** 库存明细分页 */
export function fetchDetailPage(params) {
  if (USE_MOCK) {
    const page = Number(params.page || 1)
    const size = Number(params.size || 10)
    const rows = buildMockRows(params).sort((a, b) => b.tonnage - a.tonnage)
    const start = (page - 1) * size
    const end = start + size
    return mockResolve({
      records: rows.slice(start, end).map(({ shed, coalType, tonnage, storageDays, riskLevel }) => ({
        shed,
        coalType,
        tonnage,
        storageDays,
        riskLevel
      })),
      total: rows.length,
      page,
      size
    })
  }
  return request({ url: `${base}/detailPage`, method: 'post', data: params })
}
